import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { forkJoin, Observable, Subject } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActionsSubject, Store } from '@ngrx/store';
import { ofType } from '@ngrx/effects';
import { takeUntil } from 'rxjs/operators';
import { PtCoreActions, PtCoreSelectors } from '@pt/pt-core';
import { AppAssessmentActions, ValidatorGenerator } from '@pt/pt-shared';
import { NavigationData, PtUiAppRoutes, REQUIRED_VALIDATOR_KEY } from '@pt/pt-models';
import { AppAssessmentsService, ApplicationService, FolderService } from '@pt/api-client';
import { DuplicateAppAssessmentPayload } from '../../../models';
import { AppAssessmentDocCreateRequest, AppAssessmentOverrideOnDuplicate } from '@primatoo-ms-organization';
import { Folder } from '@ms-organization-entities';
import { Application } from 'it/primatoo/openapi/api/org/schemas/application/Application';
import {
  ListAppForNewAssessmentResponse
} from 'it/primatoo/openapi/api/org/schemas/appassessment/ListAppForNewAssessmentResponse';
import { Organization } from 'it/primatoo/openapi/api/org/schemas/organization/Organization';

@Component({
  selector: 'pt-create-app-assessment-dialog',
  templateUrl: './create-app-assessment-dialog.component.html',
  styles: [`
    .form-readonly--custom {
      background: white !important;
    }

    .tree-selector {
      margin-bottom: 16px;
    }

    .tree-selector-clean {
      position: absolute;
      right: 7px;
      font-size: 7px !important;
      top: 8px;
    }

    .loader-container {
      height: 175px;
      display: grid;
      place-items: center;
    }
  `]
})
export class CreateAppAssessmentDialogComponent implements OnInit, OnDestroy {

  @Input() openOnSuccess = true;

  @Input() dialogMode: 'CREATE' | 'DUPLICATE' = 'CREATE';
  @Input() duplicateAppAssessmentPayload: DuplicateAppAssessmentPayload;

  @Input() selectedFolderId: number;
  @Input() selectedApplicationId: number;

  @ViewChild('popContentApplication') popContentApplication;

  public org$: Observable<Organization> = this.store.select(
    PtCoreSelectors.organization
  );
  private editInternalIdEnable = false;

  public folders: NavigationData[];
  public applications: NavigationData[];

  public selectedFolder: NavigationData;
  public selectedApplication: NavigationData;

  public loading = false;

  public formGroup: FormGroup;
  public REQUIRED = ValidatorGenerator.generateWidgetValidator(REQUIRED_VALIDATOR_KEY);
  private fields: string[] = [];
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public store: Store,
    public activeModal: NgbActiveModal,
    public actionsSubject: ActionsSubject,
    private folderService: FolderService,
    private applicationService: ApplicationService,
    private appAssessmentsService: AppAssessmentsService
  ) {
  }

  setLoading(value: boolean, withDelay = false) {
    if (!withDelay) {
      this.loading = value;
    } else {
      setTimeout(() => {
        this.loading = value;
      }, 300);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.setLoading(true);

    this.org$.pipe(takeUntil(this.destroy$)).subscribe((org) => {
      this.editInternalIdEnable = true; // org.editInternalIdEnable;
      this.alignDisabled();
    });

    switch (this.dialogMode) {
      case 'CREATE':
        this.initCreateNavigationData();
        break;
      case "DUPLICATE":
        this.initDuplicateNavigationData();
        break;
    }
    this.actionsSubject.pipe(
      ofType(
        AppAssessmentActions.createAppAssessmentSuccess,
      ),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      if (this.openOnSuccess) {
        this.store.dispatch(PtCoreActions.navigateTo({
          route: [
            PtUiAppRoutes.BUSINESS_MANAGEMENT,
            PtUiAppRoutes.APP_ASSESSMENTS,
            PtUiAppRoutes.EDIT,
            action?.id.toString()
          ]
        }));
      }
      this.activeModal.close(true);
    });

    this.actionsSubject.pipe(
      ofType(
        AppAssessmentActions.duplicateAppAssessmentSuccess,
      ),
      takeUntil(this.destroy$)
    ).subscribe((action) => {

      if (this.openOnSuccess) {
        this.store.dispatch(PtCoreActions.navigateTo({
          route: [
            PtUiAppRoutes.BUSINESS_MANAGEMENT,
            PtUiAppRoutes.APP_ASSESSMENTS,
            PtUiAppRoutes.EDIT,
            action.response?.id.toString()
          ]
        }));
      }
      this.activeModal.close(true);
    });
  }

  private initCreateNavigationData() {
    forkJoin({
      folders: this.folderService.findAllFolders(),
      applications: this.applicationService.findAll()
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe((response: { folders: Folder[], applications: Application[] }) => {
      if (response.folders) {
        this.folders = response.folders.map(f => NavigationData.folderToNavigationData(f));
      }

      if (response.applications) {
        this.applications = response.applications.map(f => NavigationData.applicationToNavigationData(f));
      }
      this.initFormGroup();
    });
  }

  private initDuplicateNavigationData() {
    forkJoin({
      folders: this.folderService.findAllFolders(),
      applications: this.appAssessmentsService.availableApp()
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe((response: { folders: Folder[], applications: ListAppForNewAssessmentResponse }) => {
      if (response.folders) {
        this.folders = response.folders.map(f => NavigationData.folderToNavigationData(f));
      }

      if (response.applications) {
        this.applications = NavigationData.listAppForNewAssessmentResponseToNavigationData(response.applications);
      }
      this.initFormGroup();
    });
  }

  private initFormGroup() {
    switch (this.dialogMode) {
      case 'CREATE':
        if (this.selectedFolderId !== null && this.selectedFolderId !== undefined && this.folders) {
          this.selectedFolder = this.folders?.find((f) => f.id == this.selectedFolderId);
        }

        if (this.selectedApplicationId !== null && this.selectedApplicationId !== undefined && this.applications) {
          this.selectedApplication = this.applications?.find((f) => f.id == this.selectedApplicationId);
        }

        this.formGroup = new FormGroup({
          name: new FormControl(null, [Validators.required, Validators.maxLength(128)]),
          description: new FormControl(null),
          internalId: new FormControl(null),
          applicationId: new FormControl(this.selectedApplication?.name, [Validators.required]),
        });
        this.alignDisabled();

        break;
      case 'DUPLICATE':
        this.selectedFolder = this.folders?.find((f) => f.id == this.duplicateAppAssessmentPayload?.payload?.parentId);
        this.selectedApplication = undefined; //this.applications.find((f) => f.id == this.duplicateAppAssessmentPayload?.payload?.applicationId);

        this.appAssessmentsService.getToOverride().pipe(
          takeUntil(this.destroy$)
        ).subscribe((response: AppAssessmentOverrideOnDuplicate) => {
          this.fields = Object.keys(response).filter(r => response[r] === true);

          this.formGroup = new FormGroup({
            name: new FormControl(null, [Validators.required, Validators.maxLength(128)]),
            description: new FormControl(this.duplicateAppAssessmentPayload?.payload?.description),
            internalId: new FormControl(null),
            applicationId: new FormControl(this.selectedApplication?.name, [Validators.required]),
          });
          this.alignDisabled();

        });
        break;
    }
    this.setLoading(false, true);
  }

  private alignDisabled() {
    if (this.formGroup) {
      if (this.editInternalIdEnable) {
        this.formGroup.controls.internalId.enable();
      } else {
        this.formGroup.controls.internalId.disable()
      }
    }
  }

  checkUsedField(field: string): boolean {
    switch (this.dialogMode) {
      case 'CREATE':
        return true;
      case 'DUPLICATE':
        return this.fields?.includes(field);
    }
    return true;
  }

  confirm() {
    const request = {
      applicationId: this.selectedApplication?.id,
      description: this.formGroup.controls.description.value,
      internalId: this.formGroup.controls.internalId.value,
      name: this.formGroup.controls.name.value,
      parentId: this.selectedFolder ? this.selectedFolder?.id : null
    } as AppAssessmentDocCreateRequest;

    switch (this.dialogMode) {
      case 'CREATE':
        this.store.dispatch(AppAssessmentActions.createAppAssessment({
          request: request
        }));
        break;

      case 'DUPLICATE':
        this.store.dispatch(AppAssessmentActions.duplicateAppAssessment({
          id: this.duplicateAppAssessmentPayload?.payload?.docId,
          request: request
        }));
        break;
    }
  }

  selectApplication(resource: NavigationData) {
    this.popContentApplication.close();
    this.selectedApplication = resource;
    this.formGroup.controls.applicationId.setValue(this.selectedApplication?.name);
  }

  chooseApplication() {
    this.popContentApplication.open();
  }

  cleanApplication() {
    this.formGroup?.controls?.applicationId?.setValue(null);
    this.selectedApplication = null;
  }

}
